import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import AnimeCard from "@/components/feature/AnimeCard";
import usePageMeta from "@/hooks/usePageMeta";
import { animeList, hotArticles } from "@/mocks/anime";

export default function Detail() {
  const { id } = useParams();
  const anime = animeList.find((a) => a.id === id);
  const article = hotArticles.find((a) => a.id === id);

  usePageMeta(
    anime ? `${anime.title} - 小水看番短篇番剧推荐` : article ? `${article.title} - 小水看番看番指南` : "详情 - 小水看番",
    anime
      ? `${anime.title}（${anime.titleEn}）共${anime.episode}集、每集${anime.episodeMin}分钟，${anime.synopsis}`
      : article
      ? article.excerpt
      : "小水看番短篇番剧详情",
  );

  useEffect(() => {
    if (!anime && !article) return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: anime ? anime.title : article?.title,
      description: anime ? anime.synopsis : article?.excerpt,
      datePublished: article ? article.date : `${anime?.year}-01-01`,
      author: { "@type": "Organization", name: "小水看番" },
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [anime, article]);

  const related = useMemo(() => {
    if (!anime) return animeList.slice(0, 4);
    return animeList.filter((a) => a.id !== anime.id && a.genres.some((g) => anime.genres.includes(g))).slice(0, 4);
  }, [anime]);

  if (!anime && !article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="container-x flex flex-col items-center pt-32 pb-24 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950">内容不存在或已被下架</h1>
          <p className="mt-3 text-foreground-600">这部短番可能已被移除，去看看其他泡面番吧。</p>
          <Link to="/biaoqian" className="btn-pill mt-6 bg-primary-500 text-background-50 hover:bg-primary-600">
            返回榜单
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-24 pb-16">
        {anime ? (
          <>
            <section className="container-x">
              <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
                <Link to="/" className="hover:text-primary-600">首页</Link>
                <i className="ri-arrow-right-s-line"></i>
                <Link to="/biaoqian" className="hover:text-primary-600">分类榜单</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-foreground-700">{anime.title}</span>
              </nav>

              <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12">
                <div className="lg:col-span-4">
                  <div className="sticky top-24 overflow-hidden rounded-3xl border border-background-200 bg-background-50">
                    <img
                      src={anime.image}
                      alt={`${anime.title} 短篇番剧海报`}
                      className="aspect-[3/4] w-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="lg:col-span-8">
                  <div className="flex flex-wrap items-center gap-2">
                    {anime.genres.map((g) => (
                      <span key={g} className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
                        {g}
                      </span>
                    ))}
                    <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-700">
                      {anime.status}
                    </span>
                  </div>
                  <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
                    {anime.title}
                  </h1>
                  <p className="mt-1.5 text-sm text-foreground-500">{anime.titleEn}</p>

                  <div className="mt-6 flex flex-wrap gap-3">
                    {[
                      { icon: "ri-calendar-line", label: "年份", value: `${anime.year} · ${anime.season}` },
                      { icon: "ri-play-list-2-line", label: "集数", value: `${anime.episode} 集` },
                      { icon: "ri-time-line", label: "单集", value: `${anime.episodeMin} 分钟` },
                      { icon: "ri-speed-line", label: "总时长", value: `约 ${anime.totalMin} 分钟` },
                    ].map((m) => (
                      <div key={m.label} className="flex items-center gap-2 rounded-xl bg-background-100 px-4 py-2.5">
                        <i className={`${m.icon} text-primary-600`}></i>
                        <span className="text-xs text-foreground-500">{m.label}</span>
                        <span className="text-sm font-semibold text-foreground-950">{m.value}</span>
                      </div>
                    ))}
                    <div className="flex items-center gap-2 rounded-xl bg-accent-100 px-4 py-2.5">
                      <i className="ri-star-fill text-secondary-500"></i>
                      <span className="text-sm font-bold text-foreground-950">{anime.rating}</span>
                      <span className="text-xs text-foreground-500">小水评分</span>
                    </div>
                  </div>

                  <div className="mt-8">
                    <h2 className="font-heading text-lg font-bold text-foreground-950">剧情简介</h2>
                    <p className="mt-3 leading-relaxed text-foreground-700">{anime.synopsis}</p>
                  </div>

                  <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="rounded-2xl border border-primary-100 bg-primary-50 p-5">
                      <h2 className="flex items-center gap-2 font-heading text-base font-bold text-primary-700">
                        <i className="ri-heart-line"></i>小水推荐理由
                      </h2>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-700">{anime.why}</p>
                    </div>
                    <div className="rounded-2xl border border-accent-100 bg-accent-50 p-5">
                      <h2 className="flex items-center gap-2 font-heading text-base font-bold text-accent-700">
                        <i className="ri-timer-line"></i>最适合看的场景
                      </h2>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-700">{anime.bestScene}</p>
                    </div>
                  </div>

                  <div className="mt-6 flex flex-wrap gap-2">
                    {anime.tags.map((t) => (
                      <Link
                        key={t}
                        to={`/biaoqian?q=${encodeURIComponent(t)}`}
                        className="rounded-full border border-background-200 bg-background-50 px-3 py-1 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
                      >
                        #{t}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section className="container-x mt-14">
              <div className="flex items-end justify-between">
                <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">猜你喜欢</h2>
                <Link to="/biaoqian" className="text-sm font-medium text-primary-600 hover:text-primary-700">
                  更多短番 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
                {related.map((a) => (
                  <AnimeCard key={a.id} anime={a} />
                ))}
              </div>
            </section>
          </>
        ) : (
          article && (
            <article className="container-x max-w-3xl">
              <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
                <Link to="/" className="hover:text-primary-600">首页</Link>
                <i className="ri-arrow-right-s-line"></i>
                <Link to="/biaoqian" className="hover:text-primary-600">看番指南</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-foreground-700">{article.title}</span>
              </nav>
              <span className="mt-6 inline-flex rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
                {article.tag}
              </span>
              <h1 className="mt-3 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-3xl">
                {article.title}
              </h1>
              <div className="mt-4 flex items-center gap-4 text-sm text-foreground-500">
                <span>小水看番</span>
                <span>·</span>
                <span>{article.date}</span>
                <span>·</span>
                <span>阅读 {article.readMin} 分钟</span>
              </div>
              <img
                src={article.image}
                alt={article.title}
                className="mt-6 aspect-[16/9] w-full rounded-2xl object-cover object-top"
              />
              <div className="mt-8 space-y-5">
                {article.content.map((para, i) => (
                  <p key={i} className="leading-loose text-foreground-700">{para}</p>
                ))}
              </div>
            </article>
          )
        )}
      </main>
      <Footer />
    </div>
  );
}